import { request } from "./http";
import { buildReactionTogglePayload, REACTION_TARGET_TYPES } from "../ui/reactions/reactionModel";

const PSY_CACHE = new Map(); // id:number -> raw psychologist payload
const PSY_INFLIGHT = new Map(); // id:number -> Promise<payload|null>
const PSY_MISSING = new Set(); // id:number -> known missing (404/null)

function normalizePsychologistId(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function parsePsychologistIdToken(value) {
    const raw = String(value ?? "").trim();
    if (!raw) return null;
    const token = raw.split("-")[0];
    return normalizePsychologistId(token);
}

function normalizeRouteToken(value) {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    const id = parsePsychologistIdToken(raw);
    if (id != null) return String(id);
    return raw.toLowerCase();
}

function readPsychologistId(raw) {
    if (!raw || typeof raw !== "object") return null;
    return normalizePsychologistId(
        raw.id ??
        raw.userId ??
        raw.user_id ??
        raw.psychologistId ??
        raw.psychologist_id
    );
}

function cachePsychologist(raw) {
    const id = readPsychologistId(raw);
    if (id == null) return null;
    PSY_CACHE.set(id, raw);
    PSY_MISSING.delete(id);
    return raw;
}

function extractItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.content)) return payload.content;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.rows)) return payload.rows;
    return [];
}

async function fetchPsychologistById(id, { forceRefresh = false } = {}) {
    const normalizedId = normalizePsychologistId(id);
    if (normalizedId == null) return null;
    if (!forceRefresh && PSY_MISSING.has(normalizedId)) {
        return null;
    }
    if (!forceRefresh && PSY_CACHE.has(normalizedId)) {
        return PSY_CACHE.get(normalizedId);
    }
    const existing = PSY_INFLIGHT.get(normalizedId);
    if (existing) return existing;

    const pending = request(`/api/psychologists/${normalizedId}`)
        .then((payload) => {
            if (!payload || typeof payload !== "object") {
                PSY_MISSING.add(normalizedId);
                return null;
            }
            return cachePsychologist(payload) || payload;
        })
        .catch(() => {
            PSY_MISSING.add(normalizedId);
            return null;
        })
        .finally(() => {
            PSY_INFLIGHT.delete(normalizedId);
        });

    PSY_INFLIGHT.set(normalizedId, pending);
    return pending;
}

async function warmPsychologistsCacheByList(ids = [], { pageSize = 200, maxPages = 5 } = {}) {
    const target = new Set(
        (ids || [])
            .map(normalizePsychologistId)
            .filter((id) => id != null)
    );
    if (!target.size) return;

    let page = 0;
    while (page < Math.max(1, Number(maxPages) || 5)) {
        let payload;
        try {
            payload = await request(`/api/psychologists?page=${page}&size=${Math.max(1, Number(pageSize) || 200)}`);
        } catch {
            break;
        }
        const items = extractItems(payload);
        if (!items.length) break;
        items.forEach(cachePsychologist);

        let allResolved = true;
        target.forEach((id) => {
            if (!PSY_CACHE.has(id)) allResolved = false;
        });
        if (allResolved) break;

        const totalPages = Number(payload?.totalPages ?? payload?.total_pages);
        const hasNextFlag = payload?.hasNext ?? payload?.has_next;
        const hasNext =
            typeof hasNextFlag === "boolean"
                ? hasNextFlag
                : Number.isFinite(totalPages)
                    ? page + 1 < totalPages
                    : items.length >= Math.max(1, Number(pageSize) || 200);
        if (!hasNext) break;
        page += 1;
    }
}

// -------- helpers: label -> range --------
function extractNumbers(label) {
    // Поддержка форматов: "2 000", "2\u00A0000", "2000", "2.000", "2,000" и т.п.
    const s = String(label || "")
        .replace(/\u00A0/g, " ")
        .replace(/[.,]/g, " ");

    const groups = s.match(/\d[\d\s]*/g) || [];
    return groups
        .map((g) => Number(String(g).replace(/\s+/g, "")))
        .filter((n) => Number.isFinite(n));
}

function parsePriceRangeFromLabel(label) {
    const t = String(label || "").toLowerCase().replace(/\u00A0/g, " ");
    const nums = extractNumbers(label);
    if (!nums.length) return null;

    // "до 2 000" => max
    if (t.includes("до") && nums.length === 1) {
        return { minPrice: null, maxPrice: nums[0] };
    }

    // "от 2 000" => min
    if (t.includes("от") && !t.includes("до") && nums.length === 1) {
        return { minPrice: nums[0], maxPrice: null };
    }

    // Диапазон: "от 2 000 до 3 000" / "2000-3000" / "2000–3000"
    if (nums.length >= 2) {
        const [a, b] = nums;
        const min = Math.min(a, b);
        const max = Math.max(a, b);
        return { minPrice: min, maxPrice: max };
    }

    return null;
}

function parseExpRangeFromLabel(label) {
    const t = String(label || "").toLowerCase().replace(/\u00A0/g, " ");
    const nums = extractNumbers(label);
    if (!nums.length) return null;

    // "до 3 лет" => max
    if (t.includes("до") && nums.length === 1) {
        return { minExp: null, maxExp: nums[0] };
    }

    // "от 15 лет" / "15+" => min only
    if (nums.length === 1 && (t.includes("+") || t.includes("от") || t.includes("лет"))) {
        return { minExp: nums[0], maxExp: null };
    }

    // Диапазон: "5-10" / "5–10" / "от 5 до 10"
    if (nums.length >= 2) {
        const [a, b] = nums;
        const min = Math.min(a, b);
        const max = Math.max(a, b);
        return { minExp: min, maxExp: max };
    }

    return null;
}

// если выбрано несколько диапазонов — делаем "объединение" в один min/max (самый широкий)
function mergeRanges(ranges, minKey, maxKey) {
    const rs = (ranges || []).filter(Boolean);
    if (!rs.length) return { [minKey]: null, [maxKey]: null };

    const mins = rs.map(r => r[minKey]).filter(v => Number.isFinite(v));
    const maxs = rs.map(r => r[maxKey]).filter(v => Number.isFinite(v));

    const min = mins.length ? Math.min(...mins) : null;
    const max = maxs.length ? Math.max(...maxs) : null;

    return { [minKey]: min, [maxKey]: max };
}

export const psychologistsApi = {
    // сюда прокидываем query из Filters: { q, themes, methods, experience, price, sort }
    list: ({ page, size, q, themes, methods, experience, price, sort } = {}) => {
        const p = Number.isFinite(page) ? page : 0;
        const s = Number.isFinite(size) ? size : 12;

        const qs = new URLSearchParams();
        qs.set("page", String(p));
        qs.set("size", String(s));

        if (q && String(q).trim()) qs.set("q", String(q).trim());
        if (sort) qs.set("sort", sort);

        // themes/methods: ожидаем массив либо объектов {id,...} либо чисел
        // Важно: на бэке чаще всего принимают themeIds/methodIds как повторяющиеся query-параметры.
        // Для совместимости отправляем и старые (themeId/methodId), и новые (themeIds/methodIds).
        (themes || []).forEach((t) => {
            const id = typeof t === "number" ? t : t?.id;
            if (id == null) return;
            qs.append("themeId", String(id));
            qs.append("themeIds", String(id));
        });

        (methods || []).forEach((m) => {
            const id = typeof m === "number" ? m : m?.id;
            if (id == null) return;
            qs.append("methodId", String(id));
            qs.append("methodIds", String(id));
        });

        // price/experience labels -> объединённые min/max
        const priceRanges = (price || []).map(parsePriceRangeFromLabel);
        const expRanges = (experience || []).map(parseExpRangeFromLabel);

        const { minPrice, maxPrice } = mergeRanges(priceRanges, "minPrice", "maxPrice");
        const { minExp, maxExp } = mergeRanges(expRanges, "minExp", "maxExp");

        if (Number.isFinite(minPrice)) qs.set("minPrice", String(minPrice));
        if (Number.isFinite(maxPrice)) qs.set("maxPrice", String(maxPrice));
        if (Number.isFinite(minExp)) qs.set("minExp", String(minExp));
        if (Number.isFinite(maxExp)) qs.set("maxExp", String(maxExp));

        return request(`/api/psychologists?${qs.toString()}`);
    },

    favourites: () => request(`/api/psychologists/favourites`),
    // Optional: if backend provides psychologist details by id.
    // Used as a fallback for the Favorites tab when /favourites returns only ids.
    get: (id, options = {}) => fetchPsychologistById(id, options),
    resolveProfile: async (routeToken, { forceRefresh = false } = {}) => {
        const normalizedToken = normalizeRouteToken(routeToken);
        if (!normalizedToken) return null;
        const byId = parsePsychologistIdToken(normalizedToken);
        if (byId != null) return fetchPsychologistById(byId, { forceRefresh });
        const payload = await request(`/api/psychologists/resolve/${encodeURIComponent(normalizedToken)}`);
        return cachePsychologist(payload) || payload;
    },
    getMany: async (ids = [], options = {}) => {
        const normalizedIds = Array.from(
            new Set((ids || []).map(normalizePsychologistId).filter((id) => id != null))
        );
        if (!normalizedIds.length) return [];

        const forceRefresh = Boolean(options?.forceRefresh);
        if (!forceRefresh) {
            const missingBeforeWarmup = normalizedIds.filter(
                (id) => !PSY_CACHE.has(id) && !PSY_MISSING.has(id)
            );
            if (missingBeforeWarmup.length) {
                await warmPsychologistsCacheByList(missingBeforeWarmup, options);
            }
        }

        const unresolved = normalizedIds.filter(
            (id) => forceRefresh || (!PSY_CACHE.has(id) && !PSY_MISSING.has(id))
        );
        if (unresolved.length) {
            const concurrency = Math.max(1, Math.min(12, Number(options?.concurrency) || 8));
            for (let i = 0; i < unresolved.length; i += concurrency) {
                const chunk = unresolved.slice(i, i + concurrency);
                // eslint-disable-next-line no-await-in-loop
                await Promise.all(chunk.map((id) => fetchPsychologistById(id, { forceRefresh })));
            }
        }

        return normalizedIds
            .map((id) => PSY_CACHE.get(id))
            .filter(Boolean);
    },
    getBlogPosts: (id, { page = 0, size = 10 } = {}) =>
        request(`/api/psychologists/${id}/blog-posts?page=${Number(page) || 0}&size=${Number(size) || 10}`),
    getTests: (id, { page = 0, size = 10 } = {}) =>
        request(`/api/psychologists/${id}/tests?page=${Number(page) || 0}&size=${Number(size) || 10}`),
    getForumAnswers: (id, { page = 0, size = 10 } = {}) =>
        request(`/api/psychologists/${id}/forum-answers?page=${Number(page) || 0}&size=${Number(size) || 10}`),
    getFaq: (id) => request(`/api/psychologists/${id}/faq`),
    getHighlights: (id) => request(`/api/psychologists/${id}/highlights`),
    getStories: (id) => request(`/api/psychologists/${id}/stories`),
    getMyStories: () => request(`/api/me/psychologist-profile/stories`),
    createMyStory: (payload) =>
        request(`/api/me/psychologist-profile/stories`, {
            method: "POST",
            json: payload || {},
        }),
    updateMyStory: (storyId, payload) =>
        request(`/api/me/psychologist-profile/stories/${storyId}`, {
            method: "PUT",
            json: payload || {},
        }),
    deleteMyStory: (storyId) =>
        request(`/api/me/psychologist-profile/stories/${storyId}`, {
            method: "DELETE",
        }),
    markStorySeen: (id, storyId) =>
        request(`/api/psychologists/${id}/stories/${storyId}/seen`, { method: "POST", json: {} }),
    markStoriesSeenBatch: (id, storyIds = []) =>
        request(`/api/psychologists/${id}/stories/seen/batch`, {
            method: "POST",
            json: { storyIds: Array.isArray(storyIds) ? storyIds : [] },
        }),
    reactStory: (id, storyId, reaction) =>
        request(`/api/psychologists/${id}/stories/${storyId}/reactions`, {
            method: "POST",
            json: buildReactionTogglePayload({
                reaction,
                targetType: REACTION_TARGET_TYPES.PSYCHOLOGIST_STORY,
                targetId: storyId,
                extra: { psychologistId: id },
            }),
        }),
    replyStory: (id, storyId, text) =>
        request(`/api/psychologists/${id}/stories/${storyId}/replies`, {
            method: "POST",
            json: {
                text: String(text || ""),
                message: String(text || ""),
            },
        }),
    getReviews: (id, { page = 0, size = 10 } = {}) =>
        request(`/api/psychologists/${id}/reviews?page=${Number(page) || 0}&size=${Number(size) || 10}`),
    getPodcasts: (id, { page = 0, size = 20 } = {}) =>
        request(`/api/public/psychologists/${id}/podcasts?page=${Number(page) || 0}&size=${Number(size) || 20}`),
    getLiteratureShelf: (id, { tab = "ALL", page = 0, size = 20 } = {}) =>
        request(
            `/api/public/psychologists/${id}/literature-shelf?tab=${encodeURIComponent(String(tab || "ALL"))}&page=${Number(page) || 0}&size=${Number(size) || 20}`
        ),
    getCalendar: (id, { from, to } = {}) => {
        const qs = new URLSearchParams();
        if (from) qs.set("from", String(from));
        if (to) qs.set("to", String(to));
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return request(`/api/psychologists/${id}/calendar${suffix}`);
    },
    addFavourite: (id) => request(`/api/psychologists/${id}/favorite`, { method: "POST" }),
    removeFavourite: (id) => request(`/api/psychologists/${id}/favorite`, { method: "DELETE" }),
    toggleFavourite: (id) => request(`/api/psychologists/${id}/favorite/toggle`, { method: "POST", json: {} }),
    subscribe: (id) => request(`/api/psychologists/${id}/subscribe`, { method: "POST", json: {} }),
    unsubscribe: (id) => request(`/api/psychologists/${id}/subscribe`, { method: "DELETE" }),
    toggleSubscribe: (id) => request(`/api/psychologists/${id}/subscribe/toggle`, { method: "POST", json: {} }),
    subscriptions: () => request(`/api/psychologists/subscriptions`),
};
